import {t} from '@lingui/macro'
import {useFormik} from 'formik'
import type {ChangeEvent} from 'react'

import {Button, Input, Stack, Text} from '@pleo-io/telescope'

import type {FormValues} from './email-input.validate'
import {validationSchema} from './email-input.validate'

export function useEmailInput({
    initialValue,
    isLoading,
    onSubmitEmail,
    nonUserError,
    inputLabel,
    buttonLabel,
}: {
    initialValue: string | null
    isLoading?: boolean
    onSubmitEmail: (value: string) => void
    nonUserError?: boolean
    inputLabel?: string
    buttonLabel?: string
}) {
    const formik = useFormik<FormValues>({
        initialValues: {email: initialValue ?? ''},
        validationSchema,
        validateOnBlur: false,
        validateOnChange: false,
        enableReinitialize: true,
        onSubmit: ({email}) => {
            onSubmitEmail(email)
        },
    })

    const setEmailDoesNotExists = () => {
        formik.setSubmitting(false)
        formik.setFieldError('email', t`Can't find this email. Try another one.`)
    }

    const reset = () => {
        formik.resetForm()
    }

    const element = (
        <form
            onSubmit={(e) => {
                e.preventDefault()
                formik.handleSubmit()
            }}
        >
            <Stack space={40} stretch>
                <Stack space={6} stretch>
                    <Input
                        value={formik.values.email}
                        isInvalid={Boolean(formik.errors.email) || nonUserError}
                        label={inputLabel ?? t`Work email`}
                        autoFocus
                        autoComplete="on"
                        name="email"
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            formik.setFieldError('email', '')
                            formik.setFieldValue('email', event.target.value.trim())
                        }}
                        placeholder={t`ie. name@email.com`}
                    />
                    {formik.errors.email && (
                        <Text color="colorContentNegative">{formik.errors.email}</Text>
                    )}
                </Stack>
                <Button
                    loading={formik.isSubmitting || isLoading}
                    type="submit"
                    variant="primary"
                    data-testid="e2e-login-continue-button"
                    disabled={formik.isSubmitting || isLoading}
                >
                    {buttonLabel ?? t`Continue with email`}
                </Button>
            </Stack>
        </form>
    )

    return {element, setEmailDoesNotExists, reset}
}
